import { Component, ComponentType, FC, lazy, ReactNode, Suspense } from 'react'
import { ErrorInline } from './error'

const lastScriptSrc = (): string => {
	const scripts = document.getElementsByTagName('script')
	return scripts.length ? scripts[scripts.length - 1].src : ''
}

/**
 * The app's own bundle - the last script while the app starts, since this
 * module is evaluated with it.
 */
const startupScriptSrc = typeof document === 'undefined' ? '' : lastScriptSrc()

/**
 * Imports a prebuilt (UMD / CommonJS) library the first time it is needed and
 * returns one export of it.
 *
 * Two things a production build needs that the dev server hides, because the
 * dev server bundles everything into one file:
 *
 * - The library's webpack runtime takes the base URL for its own chunks and
 *   fonts from the last <script> in the document when it evaluates. At
 *   start-up that is the app's bundle; later it can be a script added since -
 *   the YouTube API appends one to <body>. So while it evaluates, an inert
 *   script carrying the start-up src is put last; a `text/plain` script is
 *   never fetched or run.
 * - A split chunk of a CommonJS module exports only `default` (its
 *   `module.exports`), so the named export is undefined on the module itself.
 *   `pick` is tried on the module and then on its default.
 */
export const importUmdExport = async <M, E>(
	importer: () => Promise<M>,
	pick: (module: M) => E | undefined
): Promise<E> => {
	const base =
		startupScriptSrc && lastScriptSrc() !== startupScriptSrc
			? document.createElement('script')
			: undefined
	if (base) {
		base.type = 'text/plain'
		base.src = startupScriptSrc
		document.body.appendChild(base)
	}
	try {
		const module = await importer()
		const exported =
			pick(module) ?? pick((module as M & { default?: M }).default ?? ({} as M))
		if (exported === undefined) {
			throw new Error('The library loaded without the expected export')
		}
		return exported
	} finally {
		base?.remove()
	}
}

type BoundaryProps = {
	renderError: (retry: () => void) => ReactNode
	onRetry: () => void
	children: ReactNode
}

class LoadBoundary extends Component<BoundaryProps, { failed: boolean }> {
	state = { failed: false }

	static getDerivedStateFromError(): { failed: boolean } {
		return { failed: true }
	}

	retry = (): void => {
		this.props.onRetry()
		this.setState({ failed: false })
	}

	render(): ReactNode {
		return this.state.failed
			? this.props.renderError(this.retry)
			: this.props.children
	}
}

type LazyOptions = {
	fallback: NonNullable<ReactNode> | null
	/**
	 * Shown in place of the component when it failed. Defaults to the shared
	 * network-issues notice with a "Try again" button.
	 */
	renderError?: (retry: () => void) => ReactNode
	/** What "Try again" does after the code failed to load. */
	reloadPage?: () => void
}

const defaultRenderError = (retry: () => void): ReactNode => (
	<ErrorInline retry={retry} />
)

/**
 * `React.lazy` that survives a failure.
 *
 * With no boundary of its own the error reached the app's - so one dropped
 * request, or a deploy that replaced the chunk mid-session, took down the
 * whole room. Here the failure stays where the component is, with a retry.
 *
 * The retry depends on what failed. A component that threw while rendering
 * is rendered again. Code that failed to *load* cannot be imported again in
 * the same page: the browser caches the failed module for its URL, and
 * `React.lazy` caches the rejection too. So after a failed load "Try again"
 * reloads the page - in a live session that brings the user straight back to
 * where they were, with the code fetched afresh.
 */
export const lazyWithRetry = <P extends object>(
	load: () => Promise<ComponentType<P>>,
	{
		fallback,
		renderError = defaultRenderError,
		reloadPage = () => window.location.reload(),
	}: LazyOptions
): FC<P> => {
	let loadFailed = false
	// One lazy component for every instance, created once: a new one would be
	// a new element type, and every board on screen would remount with it.
	// Typed back to the component it resolves to: React 17's typings cannot
	// spread generic props onto a LazyExoticComponent.
	const Loaded = lazy(() =>
		load().then(
			component => ({ default: component }),
			error => {
				loadFailed = true
				throw error
			}
		)
	) as unknown as ComponentType<P>

	const LazyComponent: FC<P> = props => (
		<LoadBoundary
			renderError={renderError}
			onRetry={() => {
				// A render error needs only the boundary reset, which it does
				// itself; the loaded code is still good.
				if (loadFailed) {
					reloadPage()
				}
			}}
		>
			<Suspense fallback={fallback}>
				<Loaded {...props} />
			</Suspense>
		</LoadBoundary>
	)

	return LazyComponent
}
